import { Box, Button, Flex, Input, Spinner, Text, Tooltip } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { validateUserInfo } from "@/services/antiFraud.service";
import { useAuthCorbanStore } from "@/contexts/useAuthCorbanStore";
import { usernameFormatter } from "@/utils/usernameFormatter";

export default function LoginFormCorbanAuth({ shortHashId }: { shortHashId: string }) {
  const shortHash = shortHashId.replace("plat_", "");
  const navigate = useNavigate();
  const { setUserInfo, clearUserInfo } = useAuthCorbanStore();
  // O usuário informa o mesmo login (userId) que usou para entrar na plataforma.
  const [login, setLogin] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipLabel, setTooltipLabel] = useState("Digite seu login");
  const [isLoading, setIsLoading] = useState(false);

  const handleStart = async () => {
    const userId = login.trim();
    if (!userId) {
      setTooltipLabel("Digite seu login");
      setShowTooltip(true);
      return;
    }

    setIsLoading(true);
    try {
      clearUserInfo();
      const data = await validateUserInfo(userId);
      setUserInfo(data);
      navigate(`/${shortHash}/plat/geolocalizacao`);
    } catch {
      setTooltipLabel("Login não encontrado ou não autorizado");
      setShowTooltip(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Só libera quando há algo após o ponto (CPF + id do corban/promotora), ex.: 39228103817.1
  const isLoginComplete = /^\d{11}\.\d+$/.test(login);
  // CPF completo (11 dígitos) mas ainda sem o id do corban/promotora após o ponto.
  const isCpfWithoutAccount = /^\d{11}\.?$/.test(login);

  const loginHint =
    "Atenção: agora o acesso é pelo seu login completo, não só o CPF. Complete com o id do corban/promotora após o ponto.";

  return (
    <Flex
      width="100%"
      maxWidth="400px"
      flexDirection="column"
      align="center"
      gap={2}
      fontFamily="Lexend Deca"
      px="5%"
    >
      <Box
        width="100%"
        maxWidth="360px"
        bg="whiteAlpha.250"
        borderRadius="lg"
        borderWidth="1px"
        borderColor="whiteAlpha.500"
        px={4}
        py={3}
        boxShadow="md"
        mt={2}
      >
        <Text
          fontSize="xl"
          fontWeight="medium"
          color="white"
          textAlign="center"
          width="100%"
        >
          Vendedor, valide seu Login
        </Text>

        <Text
          fontSize="md"
          color="white"
          textAlign="center"
          opacity={0.95}
          mt={1}
        >
          Isso garantirá um ambiente seguro para você.
        </Text>
      </Box>

      <Input
        value={login}
        onChange={(e) => {
          setLogin(usernameFormatter(e.target.value));
          if (showTooltip) setShowTooltip(false);
        }}
        placeholder="Digite seu login"
        type="tel"
        variant="flushed"
        focusBorderColor="primary"
        color="white"
        fontSize="lg"
        _placeholder={{ color: "white" }}
        textShadow="0 0 5px rgba(0, 0, 0, 0.5)"
        paddingY="1rem"
        mt={4}
      />

      <Tooltip
        label={showTooltip ? tooltipLabel : loginHint}
        isOpen={showTooltip || isCpfWithoutAccount}
        hasArrow
        placement="bottom"
        bg="blackAlpha.800"
        color="white"
      >
        <Button
          backgroundColor="white"
          color="primary"
          borderRadius="full"
          width="100%"
          maxWidth="360px"
          height="3rem"
          marginTop="1.5rem"
          fontSize="lg"
          fontWeight="bold"
          onClick={handleStart}
          isDisabled={isLoading || !isLoginComplete}
          _hover={{ backgroundColor: "gray.100" }}
        >
          {isLoading ? <Spinner size="sm" color="primary" /> : "Prosseguir para biometria"}
        </Button>
      </Tooltip>
    </Flex>
  );
}
